import { objectIdString } from "@Helpers/schemas/common.schema";
import type { LayoutData } from "@ServerTypes/portal/portal.schema";
import type { ExternalLinkShareableResourceType } from "./externalLinks.api";
import type { ExternalLinkTarget } from "./CreateExternalLinkModal";

/**
 * The MOBILE-SAFE corner of the external-links feature.
 *
 * `useFileManager` is bundled by the React-Native app (packages/mobile aliases @Features to the web source
 * and imports that hook directly), so everything it pulls from this feature must survive Metro/Hermes with
 * mobile's dependency set. The two obvious homes both fail that test: the `../ExternalLinks` barrel
 * re-exports dialogs whose @CubeComponents imports reach `cubeComponent/icons`, which uses Vite-only
 * `import.meta.glob` that Hermes cannot parse (this chain broke `expo export` on 2026-08-20); and
 * `externalLinks.helpers.ts` imports dayjs, which mobile does not install.
 * ⚠️ Nothing here may gain an import beyond @Helpers and type-only references.
 */

/**
 * The resource types v1 can share, restated on the client because the runtime array lives in a server
 * module and importing it would pull server code into the bundle.
 *
 * The annotation is the safeguard: it is the union taken from the server's create-request schema, so
 * adding, removing or misspelling an entry here is a compile error rather than a silent divergence.
 */
const SHAREABLE_RESOURCE_TYPES: readonly ExternalLinkShareableResourceType[] = [
  "RESOURCE_TYPE_FILE",
  "RESOURCE_TYPE_FOLDER",
  "RESOURCE_TYPE_MERGED_FILE",
];

/** Folder resource type, so callers never spell the literal. */
export const RESOURCE_TYPE_FOLDER_VALUE: ExternalLinkShareableResourceType = "RESOURCE_TYPE_FOLDER";

/**
 * Narrow a grid row's `resourceType` to something a link may be created on.
 *
 * A real type predicate rather than a cast: estimates, schedules, contracts and transmittal rows all
 * flow through the same file-manager grid, and the server refuses them, so they must be filtered out
 * with the type system rather than assumed away.
 */
export function isShareableResourceType(value: string | undefined): value is ExternalLinkShareableResourceType {
  return !!value && SHAREABLE_RESOURCE_TYPES.some((shareable) => shareable === value);
}

/**
 * Build the `ExternalLinkTarget` the dialogs take, validating the ids at the boundary.
 *
 * Returns `null` rather than throwing or casting when anything fails to parse: this runs on grid rows and
 * details-panel selections, which legitimately include estimates, schedules, transmittals and rows whose
 * `spaceId` is only known from the surrounding folder. `objectIdString.safeParse` is what turns those
 * loose strings into the branded ids the server request type demands — the one place that conversion
 * happens. Shared by the File Manager context menu and the details-panel section so both decide "can this
 * be shared?" identically.
 */
export function buildExternalLinkTarget(input: {
  resourceId: string | undefined;
  spaceId: string | undefined;
  resourceType: string | undefined;
  name: string;
}): ExternalLinkTarget | null {
  const resourceId = objectIdString.safeParse(String(input.resourceId ?? ""));
  if (!resourceId.success) return null;

  const spaceId = objectIdString.safeParse(String(input.spaceId ?? ""));
  if (!spaceId.success) return null;

  // A predicate, not a cast: the narrowed union is what makes `resourceType` assignable below.
  if (!isShareableResourceType(input.resourceType)) return null;

  return {
    resourceId: resourceId.data,
    spaceId: spaceId.data,
    resourceType: input.resourceType,
    name: input.name,
    isFolder: input.resourceType === RESOURCE_TYPE_FOLDER_VALUE,
  };
}

/**
 * Is external sharing switched on for this portal? (PRD 2.)
 *
 * Reads `metaData.ENABLE_EXTERNAL_LINKS` off the layout payload already in redux, so gating a UI entry
 * point costs no request. Mirrors the server's `isExternalSharingEnabled` exactly, including its default:
 * `metaData` is an open key set and no existing portal document carries the key, so treating "absent" as
 * off would silently disable the feature for every current customer. Only an explicit `false` turns it
 * off. Hiding an entry point is presentation only — the server refuses creation on its own.
 */
export function isExternalSharingEnabled(layout: LayoutData): boolean {
  const flag = layout.ENABLE_EXTERNAL_LINKS;
  return typeof flag === "boolean" ? flag : true;
}
